import { useCallback } from "react";
import {
  collection,
  query,
  where,
  limit,
  startAfter,
  orderBy,
  Firestore,
  Query,
  DocumentData,
  QueryDocumentSnapshot
} from "firebase/firestore";

/**
 * Validates if a string matches the document number format: L000-000-000 (any uppercase letter)
 */
export function isValidDocumentNumber(value: string | null | undefined): boolean {
  if (!value) return false;
  return /^[A-Z]\d{3}-\d{3}-\d{3}$/.test(value);
}

export interface UseBuildQueryParams {
  db: Firestore;
  companyId: string;
  pageSize: number;
  collectionName: string;
  dateField?: string;
}

export interface FilterConfig {
  paramName: string;
  fieldIdToFilter: string;
  label: string;
}

// New useBuildQuery implementation
export function useBuildQuery({ db, companyId, pageSize, collectionName, dateField = "timestamp" }: UseBuildQueryParams) {
  return useCallback(
    (
      lastVisibleDoc: QueryDocumentSnapshot<DocumentData> | undefined,
      dates: [Date | string | null, Date | string | null] | null | undefined,
      activeFilters: Record<string, any> | null | undefined,
      filterConfig: FilterConfig[] | null | undefined
    ): Query<DocumentData> => {
      let q = collection(db, collectionName);
      const queryConstraints: any[] = [where("companyId", "==", companyId)];

      // PRECEDENCE: If documentNumber is provided and valid, skip date filtering
      const documentNumber = activeFilters?.documentNumber;
      const hasValidDocumentNumber = typeof documentNumber === 'string' && isValidDocumentNumber(documentNumber);

      // Date filtering using the configured dateField (skip if documentNumber is provided)
      if (!hasValidDocumentNumber && dates && dates[0]) {
        // Ensure dates are valid Date objects. Default endDate to startDate if not selected.
        const startDate = dates[0] instanceof Date ? new Date(dates[0]) : new Date(dates[0]);
        const endDate = (dates[1] || dates[0]) instanceof Date ? new Date(dates[1] || dates[0]) : new Date(dates[1] || dates[0]);

        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          // Set boundaries for start and end of day in local timezone
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);

          queryConstraints.push(where(dateField, ">=", startDate));
          queryConstraints.push(where(dateField, "<=", endDate));
        } else {
          console.warn("Invalid dates provided for query:", dates);
        }
      }

      // Document Number filter (exact match, takes precedence over date range)
      if (hasValidDocumentNumber && documentNumber) {
        queryConstraints.push(where("documentNumber", "==", documentNumber));
      }

      // Dynamic filters based on filterConfig and activeFilters
      if (filterConfig && Array.isArray(filterConfig) && activeFilters) {
        filterConfig.forEach(config => {
          const filterValue = activeFilters[config.paramName];
          // Skip documentNumber as it's handled above; skip null/empty values
          if (filterValue && config.paramName !== "documentNumber") {
            queryConstraints.push(where(config.fieldIdToFilter, "==", filterValue));
          }
        });
      }

      // Only order/paginate if not doing exact documentNumber lookup (which returns only 1 result)
      if (!hasValidDocumentNumber) {
        queryConstraints.push(orderBy(dateField, "desc"));

        if (lastVisibleDoc) {
          queryConstraints.push(startAfter(lastVisibleDoc));
        }

        queryConstraints.push(limit(pageSize));
      }

      return query(q, ...queryConstraints);
    },
    [db, companyId, pageSize, collectionName, dateField]
  );
}

export interface UseTripBuildQueryParams {
  db: Firestore;
  companyId: string;
  pageSize: number;
  collectionName: string;
}

export interface TripQueryFilters {
  dates?: [Date | string | null, Date | string | null] | null;
  status?: string | null;
  selectedVehicle?: string | null;
  selectedDriver?: string | null;
}

export function useTripBuildQuery({ db, companyId, pageSize, collectionName }: UseTripBuildQueryParams) {
  return useCallback(
    (
      lastVisibleDoc: QueryDocumentSnapshot<DocumentData> | undefined,
      { dates, status, selectedVehicle, selectedDriver }: TripQueryFilters
    ): Query<DocumentData> => {
      let q = collection(db, collectionName);
      const queryConstraints: any[] = [where("companyId", "==", companyId)];

      // Date filtering. Default endDate to startDate if not selected.
      if (dates && dates[0]) {
        const startDate = dates[0] instanceof Date ? new Date(dates[0]) : new Date(dates[0]);
        const endDate = (dates[1] || dates[0]) instanceof Date ? new Date(dates[1] || dates[0]) : new Date(dates[1] || dates[0]);
        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          // Set boundaries for start and end of day in local timezone
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);

          queryConstraints.push(where("pickupTimestamp", ">=", startDate));
          queryConstraints.push(where("pickupTimestamp", "<=", endDate));
        }
      }

      // Status filter
      if (status) {
        queryConstraints.push(where("status", "==", status));
      }

      // Vehicle filter
      if (selectedVehicle) {
        queryConstraints.push(where("fleetId", "==", selectedVehicle));
      }

      // Driver filter
      if (selectedDriver) {
        queryConstraints.push(where("fleetOperatorId", "==", selectedDriver));
      }

      queryConstraints.push(orderBy("pickupTimestamp", "desc"));

      if (lastVisibleDoc) {
        queryConstraints.push(startAfter(lastVisibleDoc));
      }

      queryConstraints.push(limit(pageSize));

      return query(q, ...queryConstraints);
    },
    [db, companyId, pageSize, collectionName]
  );
}

export interface UseHandleQueryResultParams<T extends { id: string | null } = any> {
  pageSize: number;
  setDocuments: React.Dispatch<React.SetStateAction<T[]>>;
  setLastVisibleDoc: React.Dispatch<React.SetStateAction<QueryDocumentSnapshot<DocumentData> | undefined>>;
  setPaginationDone: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useHandleQueryResult<T extends { id: string | null } = any>({
  pageSize,
  setDocuments,
  setLastVisibleDoc,
  setPaginationDone,
}: UseHandleQueryResultParams<T>) {
  return useCallback(
    (documentSnapshots: any, isInitialLoad?: boolean) => {
      let docs: any[] = [];
      documentSnapshots.forEach((doc: any) =>
        docs.push({ id: doc.id, ...doc.data() })
      );

      if (docs.length < pageSize) {
        setPaginationDone(true);
      }

      setDocuments((existingDocs) => {
        // Prevent duplicates if re-fetching the same data
        const existingIds = new Set(existingDocs.map((d) => d.id));
        const newDocs = docs.filter((d) => !existingIds.has(d.id));
        if (isInitialLoad) {
          return newDocs;
        }
        return [...existingDocs, ...newDocs];
      });

      if (docs.length === pageSize) { // Only set lastVisibleDoc if a full page was fetched
        setLastVisibleDoc(
          documentSnapshots.docs[documentSnapshots.docs.length - 1] // The actual last doc of this batch
        );
      } else {
        setLastVisibleDoc(undefined); // No more docs for pagination based on this query
      }
    },
    [pageSize, setDocuments, setLastVisibleDoc, setPaginationDone]
  );
}

export interface UseResetListingStateParams<T extends { id: string | null } = any> {
  setDocuments: React.Dispatch<React.SetStateAction<T[]>>;
  setInitialFetched: React.Dispatch<React.SetStateAction<boolean>>;
  setLastVisibleDoc: React.Dispatch<React.SetStateAction<QueryDocumentSnapshot<DocumentData> | undefined>>;
  setPaginationDone: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useResetListingState<T extends { id: string | null } = any>({
  setDocuments,
  setInitialFetched,
  setLastVisibleDoc,
  setPaginationDone,
}: UseResetListingStateParams<T>) {
  return useCallback(() => {
    setDocuments([]);
    setInitialFetched(false);
    setLastVisibleDoc(undefined);
    setPaginationDone(false);
  }, [setDocuments, setInitialFetched, setLastVisibleDoc, setPaginationDone]);
}
